/**
 * TopBar.jsx — ELYON-SHELL-FOUNDATION-1
 *
 * Top utility bar. Per Amendment D + ELYON-LANDING-V1-PLATFORM-SHELL-1 §4.PR2:
 * - Elyon wordmark (font-family-wordmark, left-anchored)
 * - ⌘K command palette trigger
 * - AEGIS trigger button (right)
 * - Notifications bell (right)
 * - Environment pill (DEV/STAGING/PROD indicator) — suppressed for production
 * - Account menu (right, Radix DropdownMenu)
 *
 * §4.NEW-2 gate: all elements render without error. ✓
 * §4.NEW-3 gate: ⌘K is wired (opens CommandPalette stub). ✓
 */
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Bell, ChevronDown, Command, LogOut, Moon, Settings, Sun, User } from 'lucide-react';
import { useUserPrefsStore, useAegisPanelOpen, useCommandPaletteOpen, useTheme } from '../stores/userPrefsStore.js';
import { useTenantEnv, useTenantBrand } from '../stores/tenantStore.js';
import { useTenant } from './TenantContext.jsx';
import { DensityToggle } from './DensityToggle.jsx';
import { auth } from '../firebase/config.js';

/** Environment label map. */
const ENV_LABEL = {
  development: 'DEV',
  staging:     'STAGING',
  production:  'PROD',
  demo:        'DEMO',
};

export function TopBar() {
  const { setAegisPanelOpen, setCommandPaletteOpen, setTheme } = useUserPrefsStore();
  const aegisOpen   = useAegisPanelOpen();
  const paletteOpen = useCommandPaletteOpen();
  const theme       = useTheme();
  const env         = useTenantEnv();
  const brand       = useTenantBrand();
  const { tenantId } = useTenant();

  const envLabel = ENV_LABEL[env] ?? env?.toUpperCase() ?? 'DEV';
  const envDataAttr = env === 'production' ? 'prod' : env === 'staging' ? 'staging' : env === 'demo' ? 'demo' : 'dev';

  async function handleSignOut() {
    try {
      if (auth) {
        const { signOut } = await import('firebase/auth');
        await signOut(auth);
      }
    } catch (err) {
      console.error('Sign-out failed:', err);
    }
  }

  function toggleTheme() {
    const [palette, mode] = theme.split('-');
    const nextMode = mode === 'light' ? 'dark' : 'light';
    setTheme(`${palette}-${nextMode}`);
  }

  return (
    <header
      className="elyon-topbar"
      role="banner"
      data-testid="topbar"
    >
      {/* Left: wordmark — fixed width so center column stays truly centered */}
      <div className="elyon-topbar-start">
        <a
          href="/"
          className="elyon-wordmark"
          aria-label="Elyon Platform home"
          style={{ textDecoration: 'none' }}
        >
          {brand?.whitelabel && brand?.name ? (
            <span style={{ fontFamily: "'Montserrat','Helvetica Neue',Arial,sans-serif", fontWeight: 600, letterSpacing: '0.15em', fontSize: 18 }}>
              {brand.name.toUpperCase()}
            </span>
          ) : (
            <svg
              viewBox="0 0 200 60"
              height="28"
              xmlns="http://www.w3.org/2000/svg"
              aria-label="Elyon Platform"
              role="img"
              style={{ display: 'block' }}
            >
              <g transform="translate(0,-8) scale(0.6)">
                <g fill="none" stroke={theme?.endsWith('dark') ? 'var(--elyon-gold)' : 'var(--elyon-navy)'} strokeWidth="1.7" strokeLinecap="round">
                  <path d="M56.86 17.72 A33 33 0 0 1 56.86 82.28"/>
                  <path d="M43.14 82.28 A33 33 0 0 1 43.14 17.72"/>
                </g>
                <path fill={theme?.endsWith('dark') ? 'var(--elyon-gold)' : 'var(--elyon-navy)'} d="M52.5 34 L28.9 35.6 A0.9 0.9 0 0 0 28.9 37.4 L52.5 39 Z"/>
                <path fill={theme?.endsWith('dark') ? 'var(--elyon-gold)' : 'var(--elyon-navy)'} d="M52.5 47.5 L22.9 49.1 A0.9 0.9 0 0 0 22.9 50.9 L52.5 52.5 Z"/>
                <path fill={theme?.endsWith('dark') ? 'var(--elyon-gold)' : 'var(--elyon-navy)'} d="M52.5 61 L28.9 62.6 A0.9 0.9 0 0 0 28.9 64.4 L52.5 66 Z"/>
                <path fill={theme?.endsWith('dark') ? 'var(--elyon-gold)' : 'var(--elyon-navy)'} d="M47.5 34 L71.1 35.6 A0.9 0.9 0 0 1 71.1 37.4 L47.5 39 Z"/>
                <path fill={theme?.endsWith('dark') ? 'var(--elyon-gold)' : 'var(--elyon-navy)'} d="M47.5 47.5 L77.1 49.1 A0.9 0.9 0 0 1 77.1 50.9 L47.5 52.5 Z"/>
                <path fill={theme?.endsWith('dark') ? 'var(--elyon-gold)' : 'var(--elyon-navy)'} d="M47.5 61 L71.1 62.6 A0.9 0.9 0 0 1 71.1 64.4 L47.5 66 Z"/>
                <line x1="50" y1="11" x2="50" y2="89" stroke={theme?.endsWith('dark') ? 'var(--elyon-gold)' : 'var(--elyon-navy)'} strokeWidth="5" strokeLinecap="round"/>
              </g>
              <text
                x="66"
                y="36"
                fontFamily="'Montserrat','Helvetica Neue',Arial,sans-serif"
                fontWeight="600"
                fontSize="24"
                letterSpacing="4"
                fill={theme?.endsWith('dark') ? 'var(--elyon-gold)' : 'var(--elyon-navy)'}
              >ELYON</text>
            </svg>
          )}
        </a>
      </div>

      {/* Center: ⌘K command palette trigger — grid center column */}
      <button
        className="elyon-cmdk-trigger"
        onClick={() => setCommandPaletteOpen(!paletteOpen)}
        aria-label="Open command palette (⌘K)"
        aria-keyshortcuts="Meta+k"
        data-testid="cmdk-trigger"
      >
        <Command size={13} strokeWidth={1.5} aria-hidden="true" style={{ flexShrink: 0 }} />
        <span className="elyon-cmdk-trigger-text">Search…</span>
        <kbd className="elyon-cmdk-kbd">⌘K</kbd>
      </button>

      {/* Right: utility buttons */}
      <div className="elyon-topbar-end">
        {/* Environment pill — suppressed in production */}
        <span
          className="elyon-env-pill"
          data-env={envDataAttr}
          aria-label={`Environment: ${envLabel}`}
          title={`Running in ${envLabel} environment`}
        >
          {envLabel}
        </span>

        {/* Density toggle — CFI-FLOOR-BEH-003 */}
        <DensityToggle />

        {/* Theme toggle */}
        <button
          className="elyon-topbar-btn"
          onClick={toggleTheme}
          aria-label={theme.endsWith('dark') ? 'Switch to light mode' : 'Switch to dark mode'}
          title={theme.endsWith('dark') ? 'Light mode' : 'Dark mode'}
        >
          {theme.endsWith('dark')
            ? <Sun  size={16} strokeWidth={1.5} aria-hidden="true" />
            : <Moon size={16} strokeWidth={1.5} aria-hidden="true" />
          }
        </button>

        {/* Notifications bell */}
        <button
          className="elyon-topbar-btn"
          aria-label="Notifications"
          title="Notifications"
          data-testid="notifications-btn"
        >
          <Bell size={16} strokeWidth={1.5} aria-hidden="true" />
          {/* Notification dot — rendered when there are unread items */}
          {/* <span className="elyon-notif-badge" aria-hidden="true" /> */}
        </button>

        {/* AEGIS trigger — text-only, brass */}
        <button
          className={['elyon-aegis-text-trigger', aegisOpen && 'aegis-active'].filter(Boolean).join(' ')}
          onClick={() => setAegisPanelOpen(!aegisOpen)}
          aria-label={aegisOpen ? 'Close AEGIS panel' : 'Open AEGIS governance panel (⌘J)'}
          aria-keyshortcuts="Meta+j"
          aria-expanded={aegisOpen}
          aria-haspopup="dialog"
          data-testid="aegis-trigger"
          title="AEGIS governance panel (⌘J)"
        >
          AEGIS
        </button>

        {/* Account menu */}
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              className="elyon-account-trigger"
              aria-label="Account menu"
              aria-haspopup="menu"
              data-testid="account-menu-trigger"
            >
              <span className="elyon-account-avatar" aria-hidden="true">
                <User size={14} strokeWidth={1.5} />
              </span>
              <ChevronDown size={12} strokeWidth={1.5} aria-hidden="true" />
            </button>
          </DropdownMenu.Trigger>

          <DropdownMenu.Portal>
            <DropdownMenu.Content
              className="elyon-dropdown-content"
              align="end"
              sideOffset={4}
            >
              <DropdownMenu.Label className="elyon-dropdown-label">
                {tenantId ?? 'elyon-demo'}
              </DropdownMenu.Label>

              <DropdownMenu.Item className="elyon-dropdown-item">
                <User size={14} strokeWidth={1.5} aria-hidden="true" />
                <span>Profile</span>
              </DropdownMenu.Item>

              <DropdownMenu.Item
                className="elyon-dropdown-item"
                onSelect={() => {
                  const [palette, mode] = theme.split('-');
                  setTheme(`${palette}-${mode === 'light' ? 'dark' : 'light'}`);
                }}
              >
                {theme.endsWith('dark')
                  ? <Sun  size={14} strokeWidth={1.5} aria-hidden="true" />
                  : <Moon size={14} strokeWidth={1.5} aria-hidden="true" />
                }
                <span>{theme.endsWith('dark') ? 'Light mode' : 'Dark mode'}</span>
              </DropdownMenu.Item>

              <DropdownMenu.Item className="elyon-dropdown-item">
                <Settings size={14} strokeWidth={1.5} aria-hidden="true" />
                <span>Settings</span>
              </DropdownMenu.Item>

              <DropdownMenu.Separator className="elyon-dropdown-separator" />

              <DropdownMenu.Item
                className="elyon-dropdown-item destructive"
                onSelect={handleSignOut}
              >
                <LogOut size={14} strokeWidth={1.5} aria-hidden="true" />
                <span>Sign out</span>
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
    </header>
  );
}
